import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../firebase/config';

export interface ExtractedMenuItemRow {
  category: string;
  name: string;
  price: number;
  description?: string;
}

export interface ExtractMenuResponse {
  items: ExtractedMenuItemRow[];
  warnings: string[];
  totalRows: number;
}

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const SUPPORTED_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

const functions = getFunctions(app);

const toBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const parts = result.split(',');
      resolve(parts.length > 1 ? parts[1] : result);
    };
    reader.onerror = () => reject(new Error('Unable to read image file.'));
    reader.readAsDataURL(file);
  });

const normalizeRows = (rows: unknown[]): ExtractedMenuItemRow[] => {
  const normalized: ExtractedMenuItemRow[] = [];

  rows.forEach((row) => {
    const value = row as Record<string, unknown>;
    const name = String(value.name || '').trim();
    const category = String(value.category || '').trim() || 'Uncategorized';
    const description = String(value.description || '').trim() || undefined;
    const price = Number(value.price);

    if (!name || !Number.isFinite(price) || price <= 0) {
      return;
    }

    normalized.push({
      name,
      category,
      price: Number(price.toFixed(2)),
      ...(description ? { description } : {}),
    });
  });

  return normalized;
};

class MenuImageAIService {
  async extractMenuFromImage(file: File, tenantId: string): Promise<ExtractMenuResponse> {
    if (!tenantId) {
      throw new Error('Tenant is required for menu extraction.');
    }

    if (!SUPPORTED_MIME_TYPES.includes(file.type.toLowerCase())) {
      throw new Error('Unsupported image format. Please upload JPG, PNG, or WEBP.');
    }

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      throw new Error('Image size exceeds 5MB. Please upload a smaller image.');
    }

    const imageBase64 = await toBase64(file);
    const callable = httpsCallable(functions, 'extractMenuFromImage');
    const result = await callable({
      tenantId,
      mimeType: file.type,
      imageBase64,
    });

    const data = (result.data || {}) as {
      items?: unknown[];
      warnings?: unknown[];
      totalRows?: number;
    };

    const items = normalizeRows(Array.isArray(data.items) ? data.items : []);
    const warnings = Array.isArray(data.warnings)
      ? data.warnings.map((warning) => String(warning))
      : [];

    return {
      items,
      warnings,
      totalRows: typeof data.totalRows === 'number' ? data.totalRows : items.length,
    };
  }
}

export const menuImageAIService = new MenuImageAIService();
